import React from 'react';
import { css } from '@emotion/react';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import Image from '../../primitives/image';
import Nearest from '../../franchise/nearest';
import { colors } from '../../primitives/colors';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import { breakpoints } from '../../primitives/tokens';
import type { HeroProps } from './index';
import Container from '../../primitives/grid/container';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { getSizesProp } from '../../../sourcing/data/contentstack/asset-helpers';
import { CsAssetSize } from '../../../sourcing/data/contentstack/content-item';

const styles = {
    section: css({
        padding: '100px 0 70px',
        position: 'relative',
        [breakpoints.tb]: {
            padding: '48px 0 0',
        },
    }),
    image: css({
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: 'min(480px, 80vh)',
        [breakpoints.tb]: {
            height: 'min(480px, 70vh)',
        },
        [breakpoints.mb]: {
            height: 'min(400px, 60vh)',
        },
    }),
    container: css({
        position: 'relative',
    }),
    inner: css({
        paddingBottom: 'var(--padding-bottom-dt)',
        color: 'var(--inner-color)',
        [breakpoints.mb]: {
            paddingBottom: 'var(--padding-bottom-mb)',
        },
    }),
    heroHeading: css({
        maxWidth: '896px',
        fontSize: '75px',
        lineHeight: '1',
        fontWeight: 300,
        [breakpoints.tb]: {
            fontSize: '48px',
            lineHeight: '1',
        },
        [breakpoints.mb]: {
            fontSize: '36px',
            lineHeight: '40px',
        },
    }),
    heroSubheading: css({
        fontSize: '44px',
        lineHeight: 'normal',
        fontWeight: 300,
        marginTop: '16px',
        maxWidth: '896px',
        [breakpoints.tb]: {
            fontSize: '36px',
            lineHeight: '40px',
            marginTop: '24px',
        },
    }),
    heroCopy: css({
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '0.025em',
        maxWidth: '672px',
        marginTop: '8px',
        marginBottom: '12px',
        [breakpoints.tb]: {
            fontSize: '20px',
            lineHeight: '28px',
            marginTop: '24px',
            marginBottom: '0',
        },
    }),
    buttonWrap: css({
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '40px',
    }),
    nearestWrap: css({
        position: 'relative',
    }),
};

const HeroHeading = ({
    shouldRenderH1,
    children,
    ...props
}: React.HTMLProps<HTMLHeadingElement> & {
    shouldRenderH1?: boolean
}): JSX.Element => {
    if (shouldRenderH1) {
        return <h1 {...props}>{children}</h1>;
    }
    return <h2 {...props}>{children}</h2>;
};

const FullWidthHero = (props: HeroProps): JSX.Element => {
    const {
        buttonLink, buttonLink2, copy, image, heading, subheading, hasLocationWidget, alignment, sizes,
    } = props;
    const hasImage = !!image?.url;
    const { isMicrosite } = useMicrosite();

    return (
        <section css={styles.section}>
            {hasImage && <Image css={styles.image} image={image} sizes={sizes || getSizesProp(CsAssetSize.FULL_MAX_WIDTH)} />}
            <Container css={styles.container}>
                <div
                    css={styles.inner}
                    style={{
                        '--padding-bottom-dt': hasLocationWidget ? '48px' : '0',
                        '--inner-color': hasImage ? colors.shadesWhite : 'unset',
                        '--padding-bottom-mb': hasLocationWidget ? '32px' : '0',
                        textAlign: alignment || 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: {
                            left: 'flex-start',
                            center: 'center',
                            right: 'flex-end',
                        }[alignment || 'center'],
                    } as React.CSSProperties}
                >
                    <HeroHeading css={styles.heroHeading} shouldRenderH1={!isMicrosite}>
                        {apCaseOnlyTitleTags(heading)}
                    </HeroHeading>
                    {subheading && (
                        <h2 css={styles.heroSubheading}>{apCaseOnlyTitleTags(subheading)}</h2>
                    )}
                    <div
                        css={[RichTextEditorStyles, styles.heroCopy]}
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{ __html: copy || '' }}
                    />
                    {(buttonLink?.title && buttonLink?.href) && (
                        <div css={styles.buttonWrap}>
                            <StyledButtonAsLink to={buttonLink.href}>
                                {buttonLink.title}
                            </StyledButtonAsLink>
                        </div>
                    )}
                    {(buttonLink2?.title && buttonLink2?.href) && (
                        <div css={styles.buttonWrap}>
                            <StyledButtonAsLink to={buttonLink2.href}>
                                {buttonLink2.title}
                            </StyledButtonAsLink>
                        </div>
                    )}
                </div>
            </Container>
            <div css={styles.nearestWrap}>
                <Nearest />
            </div>
        </section>
    );
};

export default FullWidthHero;
